import OfferInclusionsList from 'components/Common/Inclusions/OfferInclusionsList'
import Modal from 'LuxKit/Modal/Modal'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import { EmptyArray } from 'lib/array/arrayUtils'
import { getHotelOfferUrl } from 'lib/offer/offerPageURL'
import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import SearchTileStackInclusionsList from '../shared/SearchTileStackInclusionsList'
import { type SearchTileCancellationInclusionKind, useSearchTileCancellationInclusion } from '../shared/SearchTileFreeCancellationInclusion'
import { getCompactInclusionListLimit } from './accommodationSearchTileView'
import LoyaltyInclusions from 'loyalty/components/LoyaltyInclusions'
import useLoyaltyBenefitInclusions from 'loyalty/hooks/useLoyaltyBenefitInclusions'
import ExperiencesPassportOfferSummary from 'components/Experiences/ExperiencesPassport/ExperiencesPassportOfferSummary'
import { resolveMappedSearchFilterInclusions, shouldShowCompactMappedInclusionsOverflow, useSearchFilterCategoryInclusions } from '../shared/useSearchFilterCategoryInclusions'

interface Props {
  offer: App.BedbankOffer | App.BedbankOfferSummary
  filters?: App.OfferListFilters
  truncateAfter?: number
  hideLoyaltyInclusions?: boolean
  mapToSearchFilterCategories?: boolean
  cancellationInclusionKind?: SearchTileCancellationInclusionKind
}

function BedbankLTSSearchTileFeatures(props: Props) {
  const intl = useIntl()
  const { offer, filters, truncateAfter = 7, hideLoyaltyInclusions = false, mapToSearchFilterCategories = false, cancellationInclusionKind } = props
  const showModal = useContext(ModalContext)
  const loyaltyBenefitCount = useLoyaltyBenefitInclusions(offer).length
  const loyaltyCount = hideLoyaltyInclusions ? 0 : loyaltyBenefitCount
  const showLoyaltyInline = !hideLoyaltyInclusions && ((offer.inclusions?.length ?? 0) + loyaltyBenefitCount) <= truncateAfter
  const sourceInclusions = offer.inclusions ?? EmptyArray
  const cancellationInclusion = useSearchTileCancellationInclusion(cancellationInclusionKind)
  const mappedSearchFilterInclusions = useSearchFilterCategoryInclusions(sourceInclusions, truncateAfter)
  const displayedInclusions = useMemo(() => {
    const mapped = resolveMappedSearchFilterInclusions(
      mapToSearchFilterCategories,
      mappedSearchFilterInclusions.inclusions,
      sourceInclusions,
    )
    if (!mapToSearchFilterCategories || !cancellationInclusion) return mapped
    return [cancellationInclusion, ...mapped]
  }, [cancellationInclusion, mapToSearchFilterCategories, mappedSearchFilterInclusions.inclusions, sourceInclusions])
  const inclusionOverflowLabel = shouldShowCompactMappedInclusionsOverflow(
    mapToSearchFilterCategories,
    sourceInclusions.length,
    mappedSearchFilterInclusions.inclusions.length,
  ) ? mappedSearchFilterInclusions.showMoreLabel : undefined

  const openAllInclusionsModal = useCallback(() => {
    showModal(<Modal
      title={offer.property.name}
      primaryActionTo={getHotelOfferUrl(offer.id, offer.type)}
      primaryActionText={intl.formatMessage({
        id: 'QsPqaJ_bhA8fRQ-l-niE',
        defaultMessage: 'View offer',
      })}
    >
      <Group direction="vertical" gap={4} noDisplayWhenEmpty>
        {!!offer.inclusions?.length && <OfferInclusionsList inclusions={offer.inclusions} />}
        {!hideLoyaltyInclusions && <LoyaltyInclusions offer={offer} variant="offer-page" />}
      </Group>
    </Modal>)
  }, [showModal, offer, intl, hideLoyaltyInclusions])

  if (!offer.inclusions?.length && !loyaltyCount && !cancellationInclusionKind) {
    return mapToSearchFilterCategories ? null : <ExperiencesPassportOfferSummary offer={offer} filters={filters} variant="search-tile" />
  }

  return (
    <Group direction="vertical" gap={4}>
      {!mapToSearchFilterCategories && <ExperiencesPassportOfferSummary offer={offer} filters={filters} variant="search-tile" />}
      {(!!displayedInclusions.length || !!inclusionOverflowLabel) && <SearchTileStackInclusionsList
        inclusions={displayedInclusions}
        truncateAfter={getCompactInclusionListLimit(
          truncateAfter,
          mapToSearchFilterCategories && !!cancellationInclusion,
        )}
        additionalHiddenCount={mapToSearchFilterCategories || showLoyaltyInline ? 0 : loyaltyCount}
        overflowLabel={inclusionOverflowLabel}
        variant={mapToSearchFilterCategories ? 'small' : undefined}
        lineClamp={mapToSearchFilterCategories ? 1 : undefined}
        onShowMoreClick={openAllInclusionsModal}
      />}
      {!mapToSearchFilterCategories && showLoyaltyInline && <LoyaltyInclusions offer={offer} variant="search-tile" />}
    </Group>
  )
}

export default BedbankLTSSearchTileFeatures
